import { Color } from '@persona-id/design-tokens';
import { ReactChild } from 'react';
import { NavLink } from 'react-router-dom-v5-compat';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled, { css } from 'styled-components';

import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';

export const NAVBAR_ITEM_HEIGHT = 36;

type NavbarLinkVariant = 'primary' | 'danger';

export interface NavbarLinkProps {
  dataTest?: string;
  label: ReactChild;
  selected?: boolean;
  route?: string;
  disabled?: boolean;
  prefixIcon?: ReactChild;
  isCollapsed?: boolean;
  variant?: NavbarLinkVariant;
}

const NavbarLink = ({
  dataTest,
  isCollapsed,
  label,
  selected,
  route,
  disabled,
  prefixIcon,
  variant,
}: NavbarLinkProps) => {
  let colorVariant: ColorVariant;
  if (disabled) {
    colorVariant = 'disabled';
  } else if (variant === 'danger') {
    colorVariant = 'danger';
  } else if (selected) {
    colorVariant = 'selected';
  } else {
    colorVariant = 'primary';
  }

  const contents = (
    <ItemContainer colorVariant={colorVariant} data-test={dataTest}>
      {prefixIcon && (
        <>
          <Spacer width={1} height={0} />
          <IconStyler colorVariant={colorVariant}>{prefixIcon}</IconStyler>
          <Spacer width={0.5} height={0} />
        </>
      )}
      <Spacer width={1} height={0} />
      {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
      <Collapser isCollapsed={isCollapsed}>
        <Paragraph color='primary'>{label}</Paragraph>
      </Collapser>
      <Spacer width={1} height={0} />
    </ItemContainer>
  );

  if (!disabled && route?.startsWith('http')) {
    return (
      // eslint-disable-next-line react/jsx-no-target-blank
      <a href={route} target='_blank' rel='noreferrer'>
        {contents}
      </a>
    );
  } else if (!disabled && route) {
    return <NavLink to={route || '#'}>{contents}</NavLink>;
  } else {
    return <a>{contents}</a>;
  }
};

type ColorVariant = 'primary' | 'danger' | 'selected' | 'disabled';

const ItemContainer = styled.div<{ colorVariant: ColorVariant }>`
  display: flex;
  align-items: center;
  padding-top: 5px;
  padding-bottom: 5px;

  border-radius: ${BORDER_RADIUSES.rounded};
  user-select: none;

  &:hover {
    cursor: pointer;
  }

  ${({ colorVariant }) => {
    switch (colorVariant) {
      case 'danger':
        return css`
          p {
            color: ${Color.TextDanger};
          }

          &:hover {
            background-color: ${Color.CoreRed100};
            p {
              color: ${Color.TextDanger};
            }
            svg {
              color: ${Color.TextDanger};
            }
          }
        `;
      case 'selected':
        return css`
          background-color: ${Color.CorePurple100};
          p {
            color: ${Color.TextActive};
          }

          &:hover {
            svg {
              color: ${Color.BorderIconActive};
            }
          }
        `;
      case 'disabled':
        return css`
          p {
            color: ${Color.TextDisabled};
          }
        `;
      case 'primary':
      default:
        return css`
          p {
            color: ${Color.TextPrimary};
          }

          &:hover {
            background-color: ${Color.CorePurple100};
            p {
              color: ${Color.TextActive};
            }
            svg {
              color: ${Color.BorderIconActive};
            }
          }
        `;
    }
  }}
`;

const IconStyler = styled.div<{ colorVariant: ColorVariant }>`
  display: flex;
  align-items: center;

  svg {
    height: 16px;
    width: 16px;
    color: ${({ colorVariant }) => {
      switch (colorVariant) {
        case 'selected':
          return Color.BorderIconActive;
        case 'danger':
          return Color.TextDanger;
        case 'disabled':
          return Color.TextDisabled;
        case 'primary':
        default:
          return Color.BorderIconSecondary;
      }
    }};
  }
`;

const Collapser = styled.div<{ isCollapsed: boolean }>`
  width: ${(props) => (props.isCollapsed ? '0px' : '100%')};
  overflow: hidden;

  transition: 0.5s;
`;

export default NavbarLink;
